import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react'
import { api, SESSION_LOST_EVENT } from './api'
import { ADMIN_ROLES, ROLE_SUPER_ADMIN, VENUE_ROLES, type LoginResponse, type Session } from './types'

const STORAGE_KEY = 'cpd.session.v1'
/** Set when the server rejected the session; read once by the login screen. */
export const SESSION_LOST_KEY = 'cpd.session.lost'

export function takeSessionLostNotice(): boolean {
  try {
    const was = sessionStorage.getItem(SESSION_LOST_KEY) === '1'
    if (was) sessionStorage.removeItem(SESSION_LOST_KEY)
    return was
  } catch {
    return false
  }
}

function read(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Session
    return parsed?.at && parsed?.email
      ? { ...parsed, track_id: parsed.track_id ?? null }
      : null
  } catch {
    return null
  }
}

function write(session: Session | null) {
  try {
    if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* private mode — the session simply does not survive a reload */
  }
}

/** Turns any of the three login responses into a session, or null if it was a rejection. */
export function toSession(res: LoginResponse): Session | null {
  if (!res.auth || !res.at || !res.email) return null
  return {
    email: res.email,
    name: res.name ?? res.email,
    at: res.at,
    img: res.img ?? null,
    track_name: res.track_name ?? '',
    track_id: res.track_id ?? null,
    verified_name: Boolean(res.verified_name),
    id_role: res.id_role ?? 5,
  }
}

interface SessionContextValue {
  session: Session | null
  /** True once the initial localStorage read has happened — avoids a redirect flash. */
  ready: boolean
  isStaff: boolean
  isAdmin: boolean
  isSuperAdmin: boolean
  signIn: (session: Session) => void
  signOut: () => Promise<void>
  patch: (changes: Partial<Session>) => void
}

const SessionContext = createContext<SessionContextValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    setSession(read())
    setReady(true)
  }, [])

  /*
   * The server no longer recognises the token. Drop the session and leave a note for
   * the login screen; a stale session kept in storage would otherwise fail every call
   * with no way out but finding "sign out" on the Account tab.
   */
  useEffect(() => {
    const onLost = () => {
      setSession((current) => {
        if (!current) return current
        write(null)
        try { sessionStorage.setItem(SESSION_LOST_KEY, '1') } catch { /* private mode */ }
        return null
      })
    }
    window.addEventListener(SESSION_LOST_EVENT, onLost)
    return () => window.removeEventListener(SESSION_LOST_EVENT, onLost)
  }, [])

  const signIn = useCallback((next: Session) => {
    write(next)
    setSession(next)
  }, [])

  const signOut = useCallback(async () => {
    const current = read()
    // Clear locally first: the user asked to be logged out, and a failing
    // network call must not leave them staring at a still-authenticated app.
    write(null)
    setSession(null)
    if (current) {
      try {
        await api.logout(current.email, current.at)
      } catch {
        /* the token expires out of the Redis set on its own within the week */
      }
    }
  }, [])

  const patch = useCallback((changes: Partial<Session>) => {
    setSession((prev) => {
      if (!prev) return prev
      const next = { ...prev, ...changes }
      write(next)
      return next
    })
  }, [])

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      ready,
      /* Gets the staff tree: a venue display, an organiser, or a super admin. */
      isStaff: VENUE_ROLES.includes(Number(session?.id_role)),
      /* Additionally gets settings, rooms and reporting - delegate names live there. */
      isAdmin: ADMIN_ROLES.includes(Number(session?.id_role)),
      /* And user administration, which can hand any of the above to anybody. */
      isSuperAdmin: Number(session?.id_role) === ROLE_SUPER_ADMIN,
      signIn,
      signOut,
      patch,
    }),
    [session, ready, signIn, signOut, patch],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>')
  return ctx
}

/** Narrowed accessor for screens that are already behind the auth guard. */
export function useAuthed(): Session {
  const { session } = useSession()
  if (!session) throw new Error('useAuthed used outside an authenticated route')
  return session
}
